import React from "react";
import { CircleCheck } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatIbkrPortalSetupDisplayValue } from "../../utils/ibkrBrokerSetup";
import type { IbkrFlexPortalSetupItem } from "../../utils/ibkrBrokerSetup";
import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_EMPHASIS_CLASS,
  BROKER_ALERT_HEADING_CLASS,
  BROKER_ALERT_NOTE_CLASS,
  BROKER_PILL_BADGE_CLASS,
} from "./brokerAlertUi";

export const DEFAULT_IBKR_FORMAT_ITEMS: IbkrFlexPortalSetupItem[] = [
  { label: "Date format", value: "yyyyMMdd" },
  { label: "Time format", value: "HHmmss TimeZone" },
  { label: "Date/time separator", value: "semi-colon" },
];

/** Intro paragraph under an alert title. */
export const GuidanceIntro = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
  /** Accepted for call-site compatibility; styling comes from shared alert classes. */
  color?: string
}): React.ReactElement => (
  <p className={cn(BROKER_ALERT_DESC_CLASS, "mb-3", className)}>{children}</p>
)

/** Tropical Surf section heading. */
export const GuidanceSectionHeading = ({
  children,
  className,
  mt = 0,
}: {
  children: React.ReactNode
  className?: string
  color?: string
  mt?: number | string
}): React.ReactElement => (
  <p
    className={cn(
      BROKER_ALERT_HEADING_CLASS,
      "mb-3",
      mt === 0 || mt === "0" ? undefined : "mt-3",
      className
    )}
  >
    {children}
  </p>
)

/**
 * Spaced section inside collapsible broker guidance (heading + checklist rows).
 * Use inside a parent `flex flex-col gap-5` stack.
 */
export function GuidanceBlock({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-2.5">
      <GuidanceSectionHeading className="!mb-0 !mt-0">{title}</GuidanceSectionHeading>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  )
}

/** Outer stack for collapsible guidance body (intro + GuidanceBlocks). */
export function GuidanceAccordionBody({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}): React.ReactElement {
  return <div className={cn("flex flex-col gap-5", className)}>{children}</div>
}

export const ChecklistRow = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
  iconColor?: string
  labelColor?: string
}): React.ReactElement => (
  <div className={cn(BROKER_ALERT_DESC_CLASS, "flex items-start gap-2", className)}>
    <CircleCheck
      aria-hidden
      className="mt-0.5 size-4 shrink-0 text-[color:var(--brand-tropical-surf)]"
    />
    <div className="min-w-0 flex-1">{children}</div>
  </div>
)

export const FormatChecklistRow = ({
  item,
}: {
  item: IbkrFlexPortalSetupItem;
}): React.ReactElement => (
  <ChecklistRow>
    <div className="flex flex-wrap items-center gap-2">
      <span>{item.label}:</span>
      {item.value ? (
        <span className={cn(BROKER_PILL_BADGE_CLASS, "font-mono")}>
          {formatIbkrPortalSetupDisplayValue(item.value)}
        </span>
      ) : null}
    </div>
  </ChecklistRow>
);

export const IbkrTimezoneFormatNote = ({
  className,
}: {
  noteColor?: string;
  headingColor?: string;
  className?: string;
} = {}): React.ReactElement => (
  <p className={cn(BROKER_ALERT_NOTE_CLASS, "mt-1 mb-2", className)}>
    Time format must include <span className={BROKER_ALERT_EMPHASIS_CLASS}>TimeZone</span>. Exports
    without EST, EDT, UTC, or an offset (plain HHmmss) are rejected on sync and CSV import.
  </p>
);
